/**
 * Who's doing it — the one screen that was missing from a job. Used inside the New-job wizard
 * and from an open job ("Assign a company"). Four doors: a company we already use, invite a new
 * one, get quotes first, decide later. Returns a choice object; the caller performs it.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Building2, UserPlus, Scale, Clock, Check, ShieldAlert, Loader2 } from "lucide-react";
import { WField, WNote } from "@/components/fs/Wizard";
import { SendWindowNote } from "@/components/fs/SendWindowNote";
import { V } from "@/lib/vocab";

export const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

export function useCommunityVendors(tenantId) {
  const [vendors, setVendors] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/tenants/${tenantId}/field-services/vendors`)
      .then((r) => { if (alive) setVendors(r.data.vendors || []); })
      .catch(() => { if (alive) setVendors([]); });
    return () => { alive = false; };
  }, [tenantId]);
  return vendors;
}

export const ownerFor = (vendors, category) =>
  (vendors || []).find((v) => (v.link?.status === "active") && (v.link?.categories || []).includes(category))
  || (vendors || []).find((v) => (v.link?.status === "active") && !(v.link?.categories || []).length)
  || null;
const ownerIsCatchAll = (owner, category) => !!owner && !(owner.link?.categories || []).includes(category);

const Door = ({ active, onClick, icon: Icon, title, sub, testid, tone = "ink" }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-xl border p-3.5 text-left transition-colors ${active ? "border-ink bg-sand-50" : "border-sand-200 bg-white hover:border-sand-300"}`}>
    <span className="flex items-center gap-2 text-sm font-bold text-ink">
      <Icon className={`h-4 w-4 ${tone === "red" ? "text-redline-500" : "text-ink/45"}`} /> {title}
      {active && <Check className="ml-auto h-4 w-4 text-emerald-600" />}
    </span>
    <span className="mt-1 block text-xs leading-relaxed text-ink/55">{sub}</span>
  </button>
);

/* choice = { mode: "existing"|"invite"|"quotes"|"later", vendor_id, invite:{name,contact_name,phone,email}, quote_ids:[] } */
export function WhoDoesIt({ tenantId, category, choice, onChange, vendors: given, allowLater = true, autoPick = false }) {
  const loaded = useCommunityVendors(given ? null : tenantId);
  const vendors = given || loaded;
  const owner = useMemo(() => ownerFor(vendors, category), [vendors, category]);
  const linked = (vendors || []).filter((v) => v.link && v.link.status !== "removed");
  const set = (patch) => onChange({ ...choice, ...patch });
  useEffect(() => {
    if (!autoPick || !vendors || choice.mode) return;
    onChange({ ...choice, mode: linked.length ? "existing" : "invite", vendor_id: owner?.id || linked[0]?.id || "" });
  }, [autoPick, vendors]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!vendors) return <div className="grid h-24 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;

  return (
    <div className="space-y-3" data-testid="who-does-it">
      <div className="grid gap-2 sm:grid-cols-2">
        <Door testid="who-existing" icon={Building2} active={choice.mode === "existing"}
          onClick={() => set({ mode: "existing", vendor_id: choice.vendor_id || owner?.id || linked[0]?.id || "" })}
          title="A company we already use"
          sub={linked.length ? (owner ? (ownerIsCatchAll(owner, category) ? `${owner.name} takes anything without its own company here — pre-picked.` : `${owner.name} handles ${CAT_LABEL[category] || "this"} here — pre-picked.`) : `Pick from the ${linked.length} compan${linked.length === 1 ? "y" : "ies"} on this ${V.lc("org")}'s list.`)
            : `Nobody on this ${V.lc("org")}'s list yet.`} />
        <Door testid="who-invite" icon={UserPlus} active={choice.mode === "invite"} onClick={() => set({ mode: "invite" })}
          title="Invite a company"
          sub="Name and a phone or email. They get the setup link now and this job the moment their paperwork clears." />
        <Door testid="who-quotes" icon={Scale} active={choice.mode === "quotes"} onClick={() => set({ mode: "quotes", quote_ids: choice.quote_ids || [] })}
          title="Get quotes first"
          sub="Ask one to three companies from your list to price it. Nothing is dispatched until you pick one." />
        {allowLater && (
          <Door testid="who-later" icon={Clock} active={choice.mode === "later"} onClick={() => set({ mode: "later" })}
            title="Decide later"
            sub="Log the job now. It sits under Work orders marked 'no company yet' with these same buttons." />
        )}
      </div>

      {choice.mode === "existing" && (
        linked.length ? (
          <WField label="Which company">
            <select data-testid="who-existing-vendor" value={choice.vendor_id || ""} onChange={(e) => set({ vendor_id: e.target.value })}
              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
              <option value="">Pick a company…</option>
              {linked.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{owner?.id === v.id ? (ownerIsCatchAll(owner, category) ? ` · this ${V.lc("org")}'s catch-all` : " · handles this trade here") : ""}{v.paperwork?.ok === false ? " · paperwork incomplete" : ""}
                </option>
              ))}
            </select>
          </WField>
        ) : (
          <WNote tone="amber" testid="who-existing-empty">No companies on this {V.lc("org")}'s list yet — invite one, or get quotes from the network.</WNote>
        )
      )}
      {choice.mode === "existing" && choice.vendor_id && linked.find((v) => v.id === choice.vendor_id)?.paperwork?.ok === false && (
        <WNote tone="amber" testid="who-paperwork-note">
          <ShieldAlert className="mr-1 inline h-3.5 w-3.5" />
          {linked.find((v) => v.id === choice.vendor_id)?.name} is missing paperwork (insurance, W-9 or the agreement). The job is theirs, but nobody is sent to the property until it's in — we've asked them.
        </WNote>
      )}

      {choice.mode === "invite" && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="who-invite-form">
          <WField label="Company name *">
            <input data-testid="who-invite-name" value={choice.invite?.name || ""} placeholder="ABC Plumbing"
              onChange={(e) => set({ invite: { ...(choice.invite || {}), name: e.target.value } })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
          </WField>
          <WField label="Contact name">
            <input data-testid="who-invite-contact" value={choice.invite?.contact_name || ""} placeholder="Maria at the office"
              onChange={(e) => set({ invite: { ...(choice.invite || {}), contact_name: e.target.value } })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
          </WField>
          <WField label="Mobile">
            <input data-testid="who-invite-phone" value={choice.invite?.phone || ""} placeholder="+1 512 555 0199" inputMode="tel"
              onChange={(e) => set({ invite: { ...(choice.invite || {}), phone: e.target.value } })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
          </WField>
          <WField label="Email">
            <input data-testid="who-invite-email" value={choice.invite?.email || ""} placeholder="office@abcplumbing.com" inputMode="email"
              onChange={(e) => set({ invite: { ...(choice.invite || {}), email: e.target.value } })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
          </WField>
          <p className="text-xs text-ink/50 sm:col-span-2">One of mobile or email is enough. If this company is already on Ask Redline for another {V.lc("org")}, we link them instead of creating a duplicate.</p>
          <div className="sm:col-span-2">
            <SendWindowNote tenantId={tenantId} testid="who-invite-window" sendNow={choice.invite?.send === "now"}
              onChange={(on) => set({ invite: { ...(choice.invite || {}), send: on ? "now" : "auto" } })} />
          </div>
        </div>
      )}

      {choice.mode === "quotes" && (
        linked.length ? (
          <div data-testid="who-quotes-list">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Ask these companies</div>
            <div className="flex flex-wrap gap-1.5">
              {linked.map((v) => {
                const on = (choice.quote_ids || []).includes(v.id);
                return (
                  <button key={v.id} type="button" data-testid={`who-quote-${v.id}`}
                    onClick={() => set({ quote_ids: on ? choice.quote_ids.filter((x) => x !== v.id) : [...(choice.quote_ids || []), v.id].slice(0, 3) })}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
                    {v.name}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-ink/50">They get a short request with what and where, and price it on their phone. You pick the winner under Projects; the job stays right here.</p>
          </div>
        ) : (
          <WNote tone="amber" testid="who-quotes-empty">Nobody on this {V.lc("org")}'s list yet to ask. Invite a company first, or open a full bid request under Start something → Out for bids.</WNote>
        )
      )}
    </div>
  );
}

export function whoBlocked(choice) {
  if (!choice?.mode) return "Say who's doing it.";
  if (choice.mode === "existing" && !choice.vendor_id) return "Pick the company.";
  if (choice.mode === "invite") {
    if (!(choice.invite?.name || "").trim()) return "Company name is required.";
    if (!((choice.invite?.phone || "").trim() || (choice.invite?.email || "").trim())) return "A mobile or an email is needed to send the invite.";
  }
  if (choice.mode === "quotes" && !(choice.quote_ids || []).length) return "Pick at least one company to ask.";
  return null;
}

export async function performWho(tenantId, woId, choice) {
  const base = `/tenants/${tenantId}/field-services/wos/${woId}/action`;
  if (choice.mode === "existing") return (await api.post(`${base}/assign`, { vendor_id: choice.vendor_id })).data;
  if (choice.mode === "invite") return (await api.post(`${base}/invite`, choice.invite)).data;
  if (choice.mode === "quotes") return (await api.post(`${base}/quotes`, { vendor_ids: choice.quote_ids })).data;
  return null;
}
